import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useRbac } from "@/lib/rbac";
import { firstAuthorizedRoute } from "@/components/Layout";
import { toast } from "sonner";
import { FactoryIcon, ArrowRightIcon } from "@phosphor-icons/react";

export default function Login() {
  const { login, user } = useAuth();
  const { ready, administrator, can } = useRbac();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // RR02 · B-3: no automatic /seed call from the login page (it could
  // modify Administrator credentials). First-install provisioning is manual.

  // V28-003 · Permission-driven post-login landing. Administrator keeps the
  // Dashboard "/" landing; a non-admin is sent to the FIRST sidebar route they
  // actually hold VIEW for (resolved from the existing RBAC/nav authority). A
  // user with zero authorized routes gets the controlled /no-access state — no
  // loop, no "/", no permission bypass. Gated on RBAC `ready` so we redirect
  // only after permissions load. This effect runs only on the /login screen,
  // so a browser refresh on a deep authorized route is never bounced to "/".
  useEffect(() => {
    if (!user || !ready) return;
    if (administrator) { navigate("/", { replace: true }); return; }
    const target = firstAuthorizedRoute(can);
    navigate(target || "/no-access", { replace: true });
  }, [user, ready, administrator, can, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      // Redirect is handled by the RBAC-ready effect above once permissions load.
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    // Feb 2026 · Login is pinned to dark theme regardless of the app's
    // light/dark toggle so the photograph-based landing stays premium and
    // the form contrast never breaks. `data-theme="dark"` short-circuits
    // any CSS-variable overrides; the hardcoded #0a0a0a bg + explicit
    // colours below keep it consistent even before variables resolve.
    <div className="min-h-screen w-full flex" data-theme="dark" style={{ backgroundColor: "#0a0a0a" }}>
      {/* Left: image — kicks in from tablet up. Below `md` (768 px) we hide
          the panel BUT layer the same image as a low-opacity background on
          the right side so preview iframes and mobile still get on-brand
          styling instead of pure black. Feb 2026. */}
      <div className="hidden md:flex flex-1 relative overflow-hidden">
        <img
          src="/landing-hero.webp"
          alt="Focus through a camera lens — clarity, precision, and traceability"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "88% 50%" }}
        />
        {/* Directional gradient overlay — heavier now so light-mode users
            still get enough contrast for the headline. Feb 2026 · left
            column strengthened further so the "Elastic weaving, engineered."
            headline never overlaps the lens image, regardless of viewport. */}
        <div className="absolute inset-0" style={{
          background: "linear-gradient(to bottom right, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.65) 100%)"
        }} />
        <div className="absolute inset-0" style={{
          background: "linear-gradient(to right, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.6) 25%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.02) 65%)"
        }} />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full" style={{ color: "#ffffff" }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-[var(--accent)] flex items-center justify-center">
              <FactoryIcon size={22} weight="bold" style={{ color: "#ffffff" }} />
            </div>
            <div>
              <div className="font-display font-black text-xl" style={{ color: "#ffffff" }}>NARROW FABRIC- PMS.V2.8</div>
              <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: "rgba(255,255,255,0.7)" }}>Production Management System</div>
            </div>
          </div>
          <div className="max-w-sm">
            <div className="overline mb-3" style={{ color: "rgba(255,255,255,0.85)" }}>Precision · Efficiency · Traceability</div>
            <h1 className="font-display text-4xl lg:text-5xl font-black leading-tight mb-4" style={{ color: "#ffffff", textShadow: "0 2px 16px rgba(0,0,0,0.7)" }}>
              Elastic weaving,<br />engineered.
            </h1>
            <p style={{ color: "rgba(255,255,255,0.9)", textShadow: "0 1px 8px rgba(0,0,0,0.7)" }}>
              Real-time production monitoring, delivery forecasting and AI-assisted decisions for narrow fabric manufacturers.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-6 max-w-md">
            <div>
              <div className="font-mono text-2xl font-semibold" style={{ color: "#ffffff" }}>500+</div>
              <div className="text-[11px] uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.6)" }}>Machines</div>
            </div>
            <div>
              <div className="font-mono text-2xl font-semibold" style={{ color: "#ffffff" }}>99.4%</div>
              <div className="text-[11px] uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.6)" }}>Uptime</div>
            </div>
            <div>
              <div className="font-mono text-2xl font-semibold" style={{ color: "#ffffff" }}>24/7</div>
              <div className="text-[11px] uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.6)" }}>Live Board</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right: form — hardcoded dark bg so the login stays premium even
          when the rest of the app is running in light theme. */}
      <div className="w-full md:w-[520px] flex items-center justify-center p-8" style={{ backgroundColor: "#0a0a0a" }}>
        <div className="w-full max-w-sm">
          <div className="md:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-sm bg-[var(--accent)] flex items-center justify-center">
              <FactoryIcon size={22} weight="bold" style={{ color: "#ffffff" }} />
            </div>
            <div className="font-display font-black text-xl" style={{ color: "#ffffff" }}>NARROW FABRIC- PMS.V2.8</div>
          </div>

          <div className="overline mb-2" style={{ color: "rgba(255,255,255,0.6)" }}>Sign in</div>
          <h2 className="font-display text-3xl font-black tracking-tight mb-1" style={{ color: "#ffffff" }}>
            Access your plant
          </h2>
          <p className="text-sm mb-8" style={{ color: "rgba(255,255,255,0.55)" }}>
            Continue to the production board and dashboards.
          </p>

          <form onSubmit={submit} className="space-y-4" autoComplete="off">
            {/* Honeypot inputs — Chrome/Edge/Safari ignore autocomplete="off"
                on real login forms, so we prepend two hidden decoy fields
                that soak up the browser's autofill. Zero visual footprint. */}
            <input type="text" name="fakeuser" autoComplete="username" style={{ display: "none" }} tabIndex={-1} aria-hidden="true" />
            <input type="password" name="fakepass" autoComplete="new-password" style={{ display: "none" }} tabIndex={-1} aria-hidden="true" />
            <div>
              <label className="overline block mb-2" style={{ color: "rgba(255,255,255,0.6)" }}>Email</label>
              <input
                data-testid="login-email"
                type="email"
                name="mes-user-email"
                required
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-11 px-3 border rounded-sm text-sm focus:outline-none"
                style={{ backgroundColor: "#141414", borderColor: "#2a2a2a", color: "#ffffff" }}
                onFocus={(e) => { e.target.style.borderColor = "var(--accent)"; }}
                onBlur={(e) => { e.target.style.borderColor = "#2a2a2a"; }}
              />
            </div>
            <div>
              <label className="overline block mb-2" style={{ color: "rgba(255,255,255,0.6)" }}>Password</label>
              <input
                data-testid="login-password"
                type="password"
                name="mes-user-secret"
                required
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-11 px-3 border rounded-sm text-sm focus:outline-none"
                style={{ backgroundColor: "#141414", borderColor: "#2a2a2a", color: "#ffffff" }}
                onFocus={(e) => { e.target.style.borderColor = "var(--accent)"; }}
                onBlur={(e) => { e.target.style.borderColor = "#2a2a2a"; }}
              />
            </div>

            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign in"}
              {!loading && <ArrowRightIcon size={14} weight="bold" />}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
